/**
 * THE LEXICAL SLOP LAYER — deterministic, regex-only, no model call.
 *
 * Scope, and the scope is the whole point: this file catches ONLY what a
 * regex can catch honestly. Punctuation tics, stock vocabulary, and a small
 * set of banned constructions whose surface form is unambiguous.
 *
 * It deliberately does NOT implement "claims sourced", a real swap test, or
 * "robotic rhythm". Those are semantic judgments. `anatomy.ts:262` is the
 * cautionary tale: it is named `swap_test` and reads like a genericness
 * check, but it passes whenever the text merely CONTAINS a known brand name,
 * product name, competitor name, a sourced number, or a quote. The 2026-08-21
 * reproduction (a reef-safe SPF brief that said "Golden Hour" four times and
 * was generic in every other respect) passes it. A check that returns `pass`
 * on bad output is worse than no check, because it manufactures confidence.
 * If you are about to add a semantic rule here, it belongs in a separate
 * layer with a model call behind it. Do not smuggle it in as a regex.
 *
 * Vocabulary provenance, UPDATED 2026-09-15 — the three lists below are now
 * re-pointed at `SLOP_PHRASES_CORE` / `SLOP_PHRASES_STANDARD` (bottom of this
 * file), not hand-kept duplicates:
 *   - `SLOP_MARKERS` (supabase/functions/generate-content-batch/
 *     caption-directives.ts) — Deno, prompt-side ban. Derived from
 *     `SLOP_PHRASES_STANDARD` via the `_shared/content-doctrine.ts` shim.
 *   - `SLOP_PATTERNS` (src/lib/content-quality/checks/caption-substance.ts) —
 *     Node, post-hoc warn. `findSlopMarkers` there is now `findSlopPhrases`
 *     filtered to that surface's own label set.
 *   - `SLOP_VOCABULARY_PATTERN` (src/lib/content-doctrine/ad-contract.ts) —
 *     hard fail, drives `repairAdCopy`. ONE exception: its regex LITERAL
 *     stays hand-written, byte-identical to before, because
 *     `paid-copy-slop-gate.test.ts` asserts byte-for-byte parity between that
 *     literal and a second re-declaration in `paid-copy-slop-gate.ts` (a Deno
 *     bundle that cannot import `ad-contract.ts`'s `@/`-aliased imports). A
 *     literal-vs-computed-pattern check would break that parity test, and
 *     `paid-copy-slop-gate.ts` is out of scope for this change. Equivalence is
 *     instead proved by a regression test asserting `SLOP_PHRASES_CORE`
 *     matches exactly what `SLOP_VOCABULARY_PATTERN` matches, so the two
 *     cannot silently drift even though one is not syntactically generated
 *     from the other.
 *
 * ⚠️ CORRECTION, 2026-08-21. An earlier version of this comment said the Deno
 * list "can never import from `src/` at all", and used that to argue a
 * hand-matched duplicate plus a parity test was the only option. That is
 * FALSE. `supabase/functions/_shared/content-quality.ts:13` already re-exports
 * `gradeArtifact`/`gradePost` straight from `../../../src/lib/content-quality/
 * index.ts`, `generate-content-batch/index.ts:10` imports that shim, and
 * `:5` imports `src/lib/content-quality/checks/image-subject.ts` by relative
 * path directly. Deno resolves `src/` TypeScript fine as long as the module is
 * pure. THIS file is pure — zero imports, regex and string only — so it is at
 * least as portable as the one already proven in production. The edge side
 * gets a one-line re-export shim, not a copy. Do not reintroduce a twin.
 *
 * Em dashes are the reason this file exists. No rule against them existed
 * anywhere except `skills/blog-writer.ts:56` (blog and article documents
 * only) and two copy-pasted sentences in `tools/generate-ad-copy.ts:434` and
 * `lib/concept-copy/generation.ts:199`. The competitor house rule we captured
 * is stricter and simpler: "no em dashes in ad copy, ever"
 * (docs/research/competitors/marketer-com/method/METHOD-how-they-write-copy.md:157).
 */

export type SlopRuleId =
  | 'em_dash'
  | 'stock_vocabulary'
  | 'binary_contrast'
  | 'throat_clearing'
  | 'faux_insight'
  | 'colon_reveal'
  | 'announced_point'
  | 'importance_puffery'
  | 'weasel_attribution'
  | 'superficial_analysis'
  | 'emoji_heading'
  | 'slogan';

export interface SlopRule {
  id: SlopRuleId;
  re: RegExp;
  /** What the reader would call it, not what we call it internally. */
  label: string;
  /** The concrete replacement, phrased as an instruction to a writer. */
  fix: string;
}

/**
 * Stock vocabulary. Every entry here was either observed in live Mora output
 * or carried over from one of the three prior lists. Resist adding a word
 * because it "sounds like AI" — an unused rule is free, but a false positive
 * on a brand's real language is not.
 */
const STOCK_VOCABULARY: RegExp =
  /\b(?:elevate (?:your|the)|unleash|game[- ]?chang(?:er|ing)|(?:deep )?dive into|in today'?s fast[- ]paced world|in the world of|look no further|unlock the (?:power|secret)|level up your|revolutioni[sz]e|supercharge|seamless(?:ly)?|harness the power|the perfect blend of|say goodbye to|nestled in|cutting[- ]edge|state[- ]of[- ]the[- ]art|world[- ]class|best[- ]in[- ]class|next[- ]level|paradigm shift|ever[- ]evolving|tapestry|multifaceted)\b/i;

export const SLOP_RULES: SlopRule[] = [
  {
    id: 'stock_vocabulary',
    re: STOCK_VOCABULARY,
    label: 'Stock phrasing',
    fix: 'Name the specific thing the superlative is standing in for.',
  },
  {
    id: 'binary_contrast',
    // "isn't really about" and "less whether X, more whether Y" were added 2026-11-01 from the LinkedIn agent's
    // blind reviews, where each read as AI-written; they were local to that surface until then.
    re: /\b(?:it'?s not just (?:a |an |about )?[^.!?]{1,60}?(?:,| but| it'?s)|the question isn'?t [^.!?]{1,60}? it'?s|(?:isn'?t|is not) really about|less\s+(?:whether|about)\b[^.!?]{1,80}?\bmore\s+(?:whether|about))\b/i,
    label: 'Binary contrast',
    fix: 'State the second half directly and drop the setup.',
  },
  {
    id: 'binary_contrast',
    // The two-sentence form ("Speed isn't the moat. It's taste."), seen in the founder's 3 Nov 2026 review of the
    // LinkedIn voice; the one-sentence forms above miss it.
    re: /\b(?:isn'?t|is not|aren'?t|are not)\s+(?:just\s+|really\s+|only\s+)?(?:about\s+)?[^.!?\n]{1,50}[.!]\s+(?:it'?s|they'?re|that'?s)\s+(?:about\s+)?[^.!?\n]{1,50}[.!]/i,
    label: 'Binary contrast',
    fix: 'State the second half directly and drop the setup.',
  },
  {
    id: 'slogan',
    re: /\b[a-z]+(?:\s+[a-z]+)?\s+(?:is|are)\s+the\s+new\s+[a-z]+\b/i,
    label: 'Slogan',
    fix: 'Say what changed and why, in a plain sentence.',
  },
  {
    id: 'throat_clearing',
    re: /(?:^|[.!?]\s+)(?:here'?s the thing|here'?s what i mean|let me be clear|i'?ll be honest|the uncomfortable truth is)\b/i,
    label: 'Throat-clearing opener',
    fix: 'Cut the opener and start with the point.',
  },
  {
    id: 'faux_insight',
    re: /\b(?:what (?:most people|nobody) (?:gets? wrong|tells you)|the part everyone misses|this is the part most people skip|here'?s what nobody tells you)\b/i,
    label: 'Faux-insight setup',
    fix: 'Cut the setup and let the claim stand on its own.',
  },
  {
    id: 'colon_reveal',
    // Deliberately NOT case-insensitive. The tell is a lowercase dramatic
    // reveal after the colon; `The best part: Tuesday` is an ordinary label.
    // An `i` flag would also make `[a-z]` match uppercase and destroy that
    // distinction, so the leading article is spelled both ways instead.
    re: /[Tt]he (?:best|real|hard|craziest|wildest|surprising|interesting) part:\s+[a-z]/,
    label: 'Colon reveal',
    fix: 'Rewrite as a plain sentence. Colons are for lists and labels.',
  },
  {
    id: 'announced_point',
    // The prose cousin of the colon reveal: "The real question is…", "The hard part is…". Seen in nine of ten
    // LinkedIn agent drafts in the 2026-10-31 blind review. Only the announcing form: "the real estate market is"
    // and "the hard part of the drive" are left alone.
    re: /\b(?:the real (?:test|question|story|lesson|problem|bottleneck|risk|issue) (?:is|here is|will be)|the (?:hard|tricky) part (?:is|will be))\b/i,
    label: 'Announced point',
    fix: 'Say the point without announcing it.',
  },
  {
    id: 'importance_puffery',
    re: /\b(?:marks? a pivotal moment|stands? as a testament|a testament to|plays? a vital role|solidif(?:y|ies|ying) its position|underscores? its significance)\b/i,
    label: 'Importance puffery',
    fix: 'State the fact and let the reader decide whether it matters.',
  },
  {
    id: 'weasel_attribution',
    re: /\b(?:experts agree|studies show|research shows|industry reports suggest|many argue|it is widely regarded as|widely regarded as)\b/i,
    label: 'Unnamed source',
    fix: 'Name the source or cut the claim. Never invent one.',
  },
  {
    id: 'superficial_analysis',
    re: /,\s+(?:highlighting|underscoring|showcasing|reflecting|demonstrating|solidifying)\s+(?:the|its|their|a|an)\b/i,
    label: 'Trailing -ing clause',
    fix: 'Replace with the concrete consequence for the reader.',
  },
  {
    id: 'emoji_heading',
    re: /^#{1,6}\s.*\p{Extended_Pictographic}/mu,
    label: 'Emoji in a heading',
    fix: 'Remove the emoji. Formatting should follow content, not decorate it.',
  },
];

export interface SlopFinding {
  ruleId: SlopRuleId;
  label: string;
  /** The offending text, trimmed to something quotable in a UI or a prompt. */
  quote: string;
  fix: string;
}

export interface SlopReport {
  status: 'pass' | 'warn';
  findings: SlopFinding[];
  /** Raw count, before any budget is applied. */
  emDashes: number;
  /** How many em dashes this surface was allowed. */
  emDashBudget: number;
}

export interface CheckProseSlopOptions {
  /**
   * Em dashes permitted before `em_dash` is reported. Published short copy
   * (captions, ad copy, a reply to a real customer) passes 0. Long-form
   * passes a small allowance. There is deliberately no default: every call
   * site states its own budget, because a forgotten default is how a strict
   * surface silently becomes a lenient one.
   */
  emDashBudget: number;
  /**
   * Phrases the brand genuinely uses, from `approved_phrases` / voice profile.
   * A rule that matches only inside an approved phrase is not reported. This
   * mirrors the carve-out `PLAIN_LANGUAGE_CONTRACT` already makes and the one
   * marketer.com states verbatim: a ban does not apply to a brand's own
   * observed language.
   */
  approvedPhrases?: string[];
}

export const EM_DASH = '—';

function countEmDashes(text: string): number {
  let n = 0;
  for (const ch of text) if (ch === EM_DASH) n += 1;
  return n;
}

/**
 * Exported so `paid-copy-slop-gate.ts` can quote a padded window around a
 * regex match without re-implementing the same ellipsis-on-truncation logic
 * a second time — a plain `slice()` + a length-gated `quote()` cannot tell
 * the difference between "this string happens to be long" and "this window
 * was already cut off by the slice", so it silently drops the ellipsis on a
 * mid-word truncation. This function always marks a cut edge, regardless of
 * the resulting string's total length.
 */
export function quoteAround(text: string, index: number, matchLength: number): string {
  const start = Math.max(0, index - 24);
  const end = Math.min(text.length, index + matchLength + 24);
  const slice = text.slice(start, end).replace(/\s+/g, ' ').trim();
  return `${start > 0 ? '…' : ''}${slice}${end < text.length ? '…' : ''}`;
}

/**
 * True when every occurrence of `re` in `text` sits inside one of the brand's
 * approved phrases. Whole-match containment only: a partial overlap still
 * counts as a hit, so "elevate your" is not excused by an approved phrase
 * that merely shares the word "your".
 */
function fullyCoveredByApprovedPhrase(
  text: string,
  re: RegExp,
  approvedPhrases: string[],
): boolean {
  if (approvedPhrases.length === 0) return false;
  const spans = approvedPhraseSpans(text, approvedPhrases);
  if (spans.length === 0) return false;

  const global = asGlobal(re);
  let m: RegExpExecArray | null;
  let sawAny = false;
  while ((m = global.exec(text)) !== null) {
    sawAny = true;
    const hitStart = m.index;
    const hitEnd = m.index + m[0].length;
    const covered = spans.some(([s, e]) => s <= hitStart && e >= hitEnd);
    if (!covered) return false;
    if (m[0].length === 0) global.lastIndex += 1;
  }
  return sawAny;
}

/** Every place one of the brand's approved phrases occurs, case-insensitive. */
function approvedPhraseSpans(text: string, approvedPhrases: string[]): [number, number][] {
  const lower = text.toLowerCase();
  const spans: [number, number][] = [];
  for (const phrase of approvedPhrases) {
    const needle = phrase.trim().toLowerCase();
    if (!needle) continue;
    let from = 0;
    for (;;) {
      const at = lower.indexOf(needle, from);
      if (at === -1) break;
      spans.push([at, at + needle.length]);
      from = at + 1;
    }
  }
  return spans;
}

const asGlobal = (re: RegExp) =>
  new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);

/**
 * Deterministic lexical pass. Pure, synchronous, no network, no model call,
 * microseconds on realistic copy. Safe to run on a chat turn.
 */
export function checkProseSlop(text: string, options: CheckProseSlopOptions): SlopReport {
  const approvedPhrases = options.approvedPhrases ?? [];
  const emDashes = countEmDashes(text);
  const findings: SlopFinding[] = [];

  if (emDashes > options.emDashBudget) {
    findings.push({
      ruleId: 'em_dash',
      label: 'Em dashes',
      quote:
        options.emDashBudget === 0
          ? `${emDashes} em ${emDashes === 1 ? 'dash' : 'dashes'}, and this surface allows none.`
          : `${emDashes} em dashes against an allowance of ${options.emDashBudget}.`,
      fix: 'Use a comma, a period, or a colon, or rewrite the sentence.',
    });
  }

  for (const rule of SLOP_RULES) {
    const match = rule.re.exec(text);
    if (!match) continue;
    if (fullyCoveredByApprovedPhrase(text, rule.re, approvedPhrases)) continue;
    findings.push({
      ruleId: rule.id,
      label: rule.label,
      quote: quoteAround(text, match.index, match[0].length),
      fix: rule.fix,
    });
  }

  return {
    status: findings.length > 0 ? 'warn' : 'pass',
    findings,
    emDashes,
    emDashBudget: options.emDashBudget,
  };
}

export interface SlopHit extends SlopFinding {
  /** Where the hit starts in the text that was checked, and how long it is. */
  at: number;
  length: number;
}

/**
 * `checkProseSlop`'s rules, but EVERY occurrence of each, in reading order, and
 * without the em-dash count. `checkProseSlop` reports one finding per rule,
 * which suits a pass/warn gate; a writer fixing a draft needs every repeat, or
 * the third "seamless" survives two rounds of fixes (check_copy, 2026-10-02).
 */
export function findEverySlopHit(text: string, approvedPhrases: string[] = []): SlopHit[] {
  const approved = approvedPhraseSpans(text, approvedPhrases);
  const hits: SlopHit[] = [];
  for (const rule of SLOP_RULES) {
    for (const m of text.matchAll(asGlobal(rule.re))) {
      const end = m.index + m[0].length;
      if (approved.some(([s, e]) => s <= m.index && e >= end)) continue;
      hits.push({
        ruleId: rule.id,
        label: rule.label,
        quote: quoteAround(text, m.index, m[0].length),
        fix: rule.fix,
        at: m.index,
        length: m[0].length,
      });
    }
  }
  return hits.sort((a, b) => a.at - b.at);
}

export interface PunctuationRepair {
  text: string;
  /** Em dashes actually replaced. */
  replaced: number;
  /**
   * Em dashes left in place because no replacement was unambiguously correct.
   * A caller that needs zero must report these, not assume they were handled.
   */
  ambiguous: number;
}

/**
 * Replace em dashes with the punctuation a human would have used, and REFUSE
 * to guess when no single answer is clearly right.
 *
 * This is punctuation only. It never reorders, deletes, or rewords, so it
 * cannot change a claim, and it is the only repair safe to apply without
 * showing the user first.
 *
 * The conservative rule, and why each branch is safe:
 *   1. A matched PAIR inside one sentence is a parenthetical aside. Commas are
 *      the standard equivalent, EXCEPT when the enclosed span already contains
 *      a comma, where a third comma would genuinely obscure the structure.
 *      That case is left alone and counted as ambiguous.
 *   2. A LONE spaced em dash joining two clauses becomes a comma, unless the
 *      text that follows opens with a capital and the clause before it can
 *      stand alone, which reads as a full stop.
 *   3. A dash flush against a digit on both sides is a numeric range and is
 *      left alone. It is not a rhythm crutch and rewriting it would corrupt data.
 *   4. A dash opening a line is a list marker. Left alone.
 *   5. A string that is nothing but a dash is the honest-null convention used
 *      across the analytics surfaces. Never touched.
 */
export function repairPunctuation(input: string): PunctuationRepair {
  if (input.trim() === EM_DASH) {
    return { text: input, replaced: 0, ambiguous: 0 };
  }

  let replaced = 0;
  let ambiguous = 0;
  let text = input;

  // A dash the pair pass declines is swapped for a sentinel rather than left
  // as-is, so the lone pass below cannot pick up the two halves of a refusal
  // and "repair" them one at a time. That was a real bug: refusing
  // `agent — fast, cheap, ours — handles` and then splitting it into four
  // commas is the exact outcome the refusal exists to prevent. The sentinel
  // uses NUL, which cannot occur in model-generated prose.
  const SENTINEL = '\u0000ED\u0000';

  // 🔴 `[^\S\n]` (whitespace EXCEPT newline), never `\s`.
  //
  // `\s` matches a newline, so `"— first item\n— second item"` read the newline
  // as the space before a clause-joining dash and returned
  // `"— first item, second item"` — two list items COLLAPSED onto one line, the
  // newline deleted. That breaks this function's own contract ("never reorders,
  // deletes, or rewords") and defeats rule 4 above, which exists precisely to
  // leave a line-opening dash alone as a list marker: the first line's marker
  // was protected and every subsequent one was eaten.
  //
  // Found 2026-08-22 by the test written for wiring this into `provide_answer`.
  // It had been live on `suggest-reply` and `content/rewrite` too, where any
  // multi-line dash list in generated copy would have been silently flattened.
  const H = '[^\\S\\n]'; // horizontal whitespace only

  const pair = new RegExp(
    `(\\S)${H}${EM_DASH}${H}([^${EM_DASH}\\n]*?)${H}${EM_DASH}${H}(\\S)`,
    'g',
  );
  text = text.replace(pair, (_whole, before: string, inner: string, after: string) => {
    if (inner.includes(',')) {
      ambiguous += 2;
      return `${before} ${SENTINEL} ${inner} ${SENTINEL} ${after}`;
    }
    replaced += 2;
    return `${before}, ${inner}, ${after}`;
  });

  const lone = new RegExp(`(\\S)${H}${EM_DASH}${H}(\\S)`, 'g');
  text = text.replace(lone, (_whole, before: string, after: string) => {
    if (/\d/.test(before) && /\d/.test(after)) {
      ambiguous += 1;
      return `${before} ${SENTINEL} ${after}`;
    }
    replaced += 1;
    return `${before}, ${after}`;
  });

  /**
   * 🔴 THE UNSPACED DASH — `value—automated`, which is the form models
   * actually emit, and which this function could not repair until 2026-09-15.
   *
   * Both passes above require horizontal whitespace on BOTH sides. An earlier
   * version of the comment below dismissed the unspaced shape as having no
   * "obviously correct replacement" and counted it as `ambiguous`, so it was
   * reported and then shipped. Measured, session `285204bc` turn 2: a
   * `rewrite_content` result carrying `dashesRepaired: 0` next to
   * `editingPass: ["Em dashes: 1 em dash, and this surface allows none"]` — the
   * tool declares `emDashBudget: 0`, detected the violation it is named for,
   * repaired nothing, and handed the dash to the user. A gate that reports and
   * does not act is the "falsely passing" failure in the repo's own quality
   * laws, and it is the mechanical cause of the founder's _"you're using em
   * dashes and all these things"_ on 2026-09-15.
   *
   * There IS an obviously correct replacement, and it is the same one the
   * spaced passes already make: a comma. The genuinely ambiguous case is the
   * numeric range (`2020—2024`), which the `lone` pass above already guards
   * against by digit-sniffing both sides — so this reuses that exact guard
   * rather than inventing a second policy.
   *
   * Runs AFTER the spaced passes so those keep first claim on the text, and
   * mirrors their pair-before-lone order for the same reason the SENTINEL
   * exists: splitting a refused pair into two independent commas is the outcome
   * the refusal is there to prevent. `(\S)` on the left still protects a
   * line-opening `— list item` marker, which stays untouched.
   */
  /*
   * 🔴 THE REFUSAL BRANCHES CAPTURE THE SPACING AND PUT IT BACK VERBATIM.
   *
   * The two spaced passes above can hardcode a space around their sentinel
   * because they only ever match text that ALREADY had one on each side —
   * re-emitting it is lossless there. These passes match the UNSPACED shape
   * too, so the same `${before} ${SENTINEL} ${after}` would invent whitespace
   * that was never in the input: `4—6%` came back as `4 — 6%`. Caught by
   * `provide-answer-slop.test.ts`'s numeric-range case, which is precisely the
   * input the refusal exists to protect — a refusal that edits the thing it
   * refused to touch is worse than no refusal, because it looks like a pass.
   *
   * So the whitespace is captured and restored exactly, and `repairPunctuation`
   * keeps its documented contract: it never reorders, deletes, or rewords.
   */
  const tightPair = new RegExp(
    `(\\S)(${H}*)${EM_DASH}(${H}*)([^${EM_DASH}\\n]*?)(${H}*)${EM_DASH}(${H}*)(\\S)`,
    'g',
  );
  text = text.replace(
    tightPair,
    (
      _whole,
      before: string,
      ws1: string,
      ws2: string,
      inner: string,
      ws3: string,
      ws4: string,
      after: string,
    ) => {
      if (inner.includes(',')) {
        ambiguous += 2;
        return `${before}${ws1}${SENTINEL}${ws2}${inner}${ws3}${SENTINEL}${ws4}${after}`;
      }
      replaced += 2;
      return `${before}, ${inner}, ${after}`;
    },
  );

  const tightLone = new RegExp(`(\\S)(${H}*)${EM_DASH}(${H}*)(\\S)`, 'g');
  text = text.replace(
    tightLone,
    (_whole, before: string, ws1: string, ws2: string, after: string) => {
      if (/\d/.test(before) && /\d/.test(after)) {
        ambiguous += 1;
        return `${before}${ws1}${SENTINEL}${ws2}${after}`;
      }
      replaced += 1;
      return `${before}, ${after}`;
    },
  );

  // Anything still a literal em dash matched none of the handled shapes — in
  // practice a line-opening list marker, which is deliberately left alone.
  ambiguous += countEmDashes(text);

  text = text.split(SENTINEL).join(EM_DASH);

  return { text, replaced, ambiguous };
}

/**
 * Streaming-safe wrapper around `repairPunctuation` for the chat answer.
 *
 * 2026-09-06: the answer moved from a single complete `provide_answer` tool
 * argument (arrives whole, at the end) to normal assistant TEXT, which the AI
 * SDK streams as `text-delta` chunks a few characters at a time (see
 * `answer-punctuation-stream-transform.ts`, wired into `experimental_transform`
 * in `server/api/agent/index.post.ts`). `repairPunctuation` needs BOTH sides
 * of an em dash — the pair regex needs a second dash and trailing context, the
 * lone regex needs the character immediately after — to decide pair vs. lone
 * vs. ambiguous. Running it on a raw few-character delta independently would
 * almost never see that context, so the gate would silently stop catching
 * anything: the exact "quality gate that stops running" regression this
 * change was told explicitly to avoid.
 *
 * The fix is NOT to re-derive `repairPunctuation`'s decision logic in
 * streaming form (a second implementation is a second place to get it wrong).
 * It buffers only the text needed to resolve an in-flight em dash and flushes
 * everything else immediately — a normal answer (the common case: no em dash
 * at all, `PLAIN_LANGUAGE_CONTRACT` already discourages them at the source)
 * streams with only a one-word lag (see `noDashSafeBoundary`: even dash-free
 * text holds back its trailing partial word, in case the very next delta
 * turns out to be a dash that needs it as context). When a dash does appear,
 * it is resolved by handing the WHOLE withheld span to the real
 * `repairPunctuation` the moment resolution is safe:
 *   - a second dash arrives, plus one character of trailing context (the pair
 *     regex's own requirement) — resolve as a pair (or ambiguous, exactly as
 *     `repairPunctuation` would),
 *   - or `MAX_HOLD_CHARS` characters pass with no second dash — resolve as
 *     lone, so one stray unpaired dash can never freeze the rest of the
 *     stream. Deliberately NOT triggered by a sentence boundary (a period):
 *     `repairPunctuation`'s own pair regex has no concept of "sentence" — its
 *     inner-content group excludes only another em dash or a newline, so it
 *     will pair two dashes straddling a period. Resolving early on a period
 *     would diverge from that (caught by this function's own test suite:
 *     'First — with a dash. Second — with another.' pairs BOTH dashes when
 *     `repairPunctuation` runs on the whole text).
 *
 * Known, accepted trade-off: a PAIR whose two dashes are more than
 * `MAX_HOLD_CHARS` apart resolves as two independent LONE decisions instead of
 * one PAIR decision. `repairPunctuation`'s own doc comment calls a pair "a
 * parenthetical aside" — an aside that long is itself the kind of copy
 * `PLAIN_LANGUAGE_CONTRACT` already discourages, so the real-world frequency
 * of this edge is expected to be near zero.
 */
export function createStreamingPunctuationRepairer() {
  const MAX_HOLD_CHARS = 160;
  const isHorizontalWhitespace = (ch: string | undefined) =>
    ch !== undefined && ch !== '\n' && /\s/.test(ch);

  let buffer = '';
  let replaced = 0;
  let ambiguous = 0;

  function resolve(segment: string): string {
    const result = repairPunctuation(segment);
    replaced += result.replaced;
    ambiguous += result.ambiguous;
    return result.text;
  }

  /** Index of the first character that must still be held back, walking
   * backward from `dashIndex` over horizontal whitespace, THEN one more
   * character, so the withheld span keeps the `(\S)` character
   * `repairPunctuation`'s own regexes require immediately before the dash —
   * not just the whitespace that separates it. */
  function holdStartFor(dashIndex: number, text: string): number {
    let start = dashIndex;
    while (start > 0 && isHorizontalWhitespace(text[start - 1])) start--;
    if (start > 0) start--;
    return start;
  }

  /** How much of `text` (which contains no em dash) is safe to flush right
   * now. Flushing the WHOLE thing would be wrong: if it ends mid-word, the
   * very next delta could be a dash, and `holdStartFor` needs that word's
   * last character still in the buffer to find the `(\S)` it requires. Holds
   * back the trailing word (and any whitespace after it) so that character
   * is never lost — the same one-word lag `smoothStream`'s own word chunking
   * already accepts as invisible. */
  function noDashSafeBoundary(text: string): number {
    let i = text.length;
    while (i > 0 && isHorizontalWhitespace(text[i - 1])) i--;
    while (i > 0 && !isHorizontalWhitespace(text[i - 1]) && text[i - 1] !== '\n') i--;
    return i;
  }

  /** Attempt one resolution step. Returns the text safe to flush now, or
   * `null` if nothing new can be released yet (still waiting on context). */
  function step(): string | null {
    const firstDash = buffer.indexOf(EM_DASH);
    if (firstDash === -1) {
      if (!buffer) return null;
      const boundary = noDashSafeBoundary(buffer);
      if (boundary === 0) {
        // The whole buffer is one unbroken run with no word boundary yet
        // (rare — real prose breaks on spaces constantly). Cap the wait so a
        // pathological unbroken run can never withhold forever.
        if (buffer.length <= MAX_HOLD_CHARS) return null;
        const out = buffer;
        buffer = '';
        return out;
      }
      const out = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary);
      return out;
    }

    const holdStart = holdStartFor(firstDash, buffer);
    if (holdStart > 0) {
      const safe = buffer.slice(0, holdStart);
      buffer = buffer.slice(holdStart);
      return safe;
    }

    const secondDash = buffer.indexOf(EM_DASH, firstDash + 1);
    if (secondDash !== -1) {
      let i = secondDash + 1;
      while (i < buffer.length && isHorizontalWhitespace(buffer[i])) i++;
      if (i < buffer.length && buffer[i] !== '\n') {
        const cutAt = i + 1;
        const resolved = resolve(buffer.slice(0, cutAt));
        buffer = buffer.slice(cutAt);
        return resolved;
      }
      // Second dash seen, but not yet one character of trailing context —
      // keep waiting.
      return null;
    }

    // Only one dash in view, no second one yet. DELIBERATELY not resolved on
    // a sentence boundary (a period): `repairPunctuation`'s own pair regex
    // has no concept of "sentence" — its inner-content group excludes only
    // another em dash or a newline, so a period does not stop it pairing
    // dash 1 with a dash 2 that arrives after a "in view" sentence boundary.
    // Found live in this function's own test suite: 'First — with a dash.
    // Second — with another.' pairs BOTH dashes into commas when
    // `repairPunctuation` runs on the whole text — resolving on the period
    // would have wrongly treated dash 1 as lone before dash 2 ever arrived.
    // `MAX_HOLD_CHARS` is therefore the ONLY give-up trigger, and it is the
    // one documented trade-off at the top of this function: a pair whose two
    // dashes are genuinely more than `MAX_HOLD_CHARS` apart resolves as two
    // lone dashes instead of one pair.
    const waitedTooLong = buffer.length - firstDash > MAX_HOLD_CHARS;
    if (waitedTooLong) {
      const resolved = resolve(buffer);
      buffer = '';
      return resolved;
    }
    return null;
  }

  return {
    /** Feed the next raw text-delta chunk; returns the text safe to emit now
     * (possibly empty when everything is being held back). */
    push(delta: string): string {
      buffer += delta;
      let out = '';
      let next: string | null;
      while ((next = step()) !== null) out += next;
      return out;
    },
    /** Call once the underlying text stream ends. Resolves whatever remains
     * (there is no more context coming, so it is resolved exactly as
     * `repairPunctuation` would resolve it standalone) and returns it. */
    flush(): string {
      if (!buffer) return '';
      const resolved = resolve(buffer);
      buffer = '';
      return resolved;
    },
    /** Cumulative counts across every `push`/`flush` call so far, in the same
     * shape `repairPunctuation` reports for a single call. */
    stats(): { replaced: number; ambiguous: number } {
      return { replaced, ambiguous };
    },
  };
}

/**
 * `repairPunctuation` for markdown, leaving fenced code blocks untouched.
 *
 * A document artifact can legitimately contain a code fence where an em dash
 * is data, not prose. Rewriting inside one would corrupt content the user
 * pasted or the model quoted, so the fences are held out and stitched back
 * verbatim. Splitting on ``` yields alternating prose/code segments, and only
 * the even-indexed prose ones are repaired.
 */
export function repairMarkdownPunctuation(markdown: string): PunctuationRepair {
  const segments = markdown.split('```');
  let replaced = 0;
  let ambiguous = 0;
  const out = segments.map((segment, i) => {
    if (i % 2 === 1) return segment;
    const r = repairPunctuation(segment);
    replaced += r.replaced;
    ambiguous += r.ambiguous;
    return r.text;
  });
  return { text: out.join('```'), replaced, ambiguous };
}

/**
 * The em-dash allowance for a long-form artifact, by length.
 *
 * "Usually none in short copy, and only 1-2 in longer drafts when they clearly
 * help" is the guidance this implements, so a one-paragraph brief gets zero
 * and a 2000-word blog gets two. Capped deliberately: the cap is what stops a
 * long document from earning an unbounded dash budget just by being long.
 */
export function longFormEmDashBudget(text: string): number {
  return Math.min(2, Math.floor(text.length / 1500));
}

/**
 * Render a report as the numbered issues list `repairAdCopy` already consumes,
 * so a caller can hand findings straight back to a model for one revise pass
 * without inventing a second format.
 */
export function slopFindingsAsIssues(report: SlopReport): string[] {
  return report.findings.map((f) => `${f.label}: ${f.quote} Fix: ${f.fix}`);
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * PHRASE VOCABULARY — TWO TIERS, ONE SOURCE (2026-09-15).
 *
 * The three lists named in the header comment above have three DIFFERENT
 * severities, and that is the trap: flattening them into one list would make
 * `ad-contract.ts` start hard-failing ad copy on words it currently permits —
 * a real behaviour change with money consequences (`checkAdContract` drives
 * `repairAdCopy` on real spend). So there are two tiers instead of one list:
 *
 *   - CORE:     exactly what `SLOP_VOCABULARY_PATTERN` (ad-contract.ts)
 *               matches today, nothing more. Safe to HARD FAIL on.
 *   - STANDARD: the broader set for prompt bans and post-hoc warnings. A
 *               strict superset of CORE, covering the union of the three
 *               original lists (`SLOP_MARKERS`, `SLOP_PATTERNS`, and this
 *               file's own `STOCK_VOCABULARY`).
 *
 * `findSlopPhrases(text, tier)` is the one place phrase-matching happens.
 * Each entry owns its OWN regex, reproducing the exact matching behaviour of
 * whichever original list it was sourced from — this is deliberately NOT a
 * single generic "does the string contain this word" matcher, because the
 * three original lists made different, deliberate matching choices (e.g.
 * caption-substance.ts's `game[- ]changer` label excludes "-changing"; this
 * file's own `STOCK_VOCABULARY` includes it) and a generic matcher would
 * either lose or invent behaviour no caller asked for.
 *
 * CORE's own labels are intentionally FINER-GRAINED than STANDARD's grouped
 * labels (e.g. CORE has a standalone `elevate your`; STANDARD groups
 * "elevate your"/"elevate the" under one `elevate` label, matching
 * caption-substance.ts's original grouping). `findSlopPhrases` does not
 * collapse the two — a caller that needs one particular label vocabulary
 * (caption-substance.ts does) filters to its own known label set. See that
 * file for why.
 */
export type SlopPhraseTier = 'core' | 'standard';

export interface SlopPhraseMatch {
  /** Canonical display form of the phrase that matched. */
  phrase: string;
  /** The label this match is reported under — see the file comment above for
   *  why this can differ from `phrase` (a grouped label covering more than
   *  one literal phrase). */
  label: string;
}

interface SlopPhraseEntry extends SlopPhraseMatch {
  re: RegExp;
  tier: SlopPhraseTier;
}

function escapeForPhraseRegex(phrase: string): string {
  return phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/'/g, "'?");
}

/** A plain, word-bounded, case-insensitive match on the phrase's own text.
 *  Used for phrases that exist only to be LISTED (the prompt-ban surface),
 *  not matched with any special-cased grammar. */
function literalPhrase(phrase: string, tier: SlopPhraseTier = 'standard'): SlopPhraseEntry {
  return {
    phrase,
    label: phrase,
    tier,
    re: new RegExp(`\\b${escapeForPhraseRegex(phrase)}\\b`, 'i'),
  };
}

/**
 * CORE — exactly the alternatives inside `SLOP_VOCABULARY_PATTERN`
 * (ad-contract.ts), each broken out as its own entry so `findSlopPhrases`
 * can report which one hit. Union of these `.test()` calls is equivalent to
 * the single combined pattern: every alternative begins and ends on a word
 * character, so the shared `\b...\b` wrapper on the combined pattern behaves
 * identically to bounding each alternative individually. Proved against a
 * shared corpus in `__tests__/slop.test.ts`.
 */
const CORE_ENTRIES: readonly SlopPhraseEntry[] = [
  {
    phrase: 'game-changer',
    label: 'game-changer',
    tier: 'core',
    re: /\bgame[- ]?chang(?:er|ing)\b/i,
  },
  { phrase: 'revolutionary', label: 'revolutionary', tier: 'core', re: /\brevolutionary\b/i },
  { phrase: 'cutting-edge', label: 'cutting-edge', tier: 'core', re: /\bcutting[- ]edge\b/i },
  {
    phrase: 'state-of-the-art',
    label: 'state-of-the-art',
    tier: 'core',
    re: /\bstate[- ]of[- ]the[- ]art\b/i,
  },
  { phrase: 'world-class', label: 'world-class', tier: 'core', re: /\bworld[- ]class\b/i },
  { phrase: 'best-in-class', label: 'best-in-class', tier: 'core', re: /\bbest[- ]in[- ]class\b/i },
  { phrase: 'next-level', label: 'next-level', tier: 'core', re: /\bnext[- ]level\b/i },
  { phrase: 'supercharge', label: 'supercharge', tier: 'core', re: /\bsupercharge\b/i },
  {
    phrase: 'unlock the power/secret',
    label: 'unlock the power/secret',
    tier: 'core',
    re: /\bunlock the (?:power|secret)\b/i,
  },
  { phrase: 'elevate your', label: 'elevate your', tier: 'core', re: /\belevate your\b/i },
  { phrase: 'unleash', label: 'unleash', tier: 'core', re: /\bunleash\b/i },
];

/**
 * STANDARD-ONLY, grouped entries reproducing `caption-substance.ts`'s
 * original `SLOP_PATTERNS` regex + label pairs verbatim, for the labels not
 * already covered above (`unleash` and `supercharge` are shared with CORE —
 * same word, same regex, no reason for a second entry).
 */
const STANDARD_GROUPED_ENTRIES: readonly SlopPhraseEntry[] = [
  {
    phrase: 'elevate',
    label: 'elevate',
    tier: 'standard',
    re: /\belevate your\b|\belevate the\b/i,
  },
  { phrase: 'dive into', label: 'dive into', tier: 'standard', re: /\b(?:deep )?dive into\b/i },
  {
    phrase: "in today's fast-paced world",
    label: "in today's fast-paced world",
    tier: 'standard',
    re: /\bin today'?s fast[- ]paced world\b/i,
  },
  {
    phrase: 'in the world of',
    label: 'in the world of',
    tier: 'standard',
    re: /\bin the world of\b/i,
  },
  {
    phrase: 'look no further',
    label: 'look no further',
    tier: 'standard',
    re: /\blook no further\b/i,
  },
  { phrase: 'unlock the', label: 'unlock the', tier: 'standard', re: /\bunlock the\b/i },
  { phrase: 'level up your', label: 'level up your', tier: 'standard', re: /\blevel up your\b/i },
  {
    phrase: 'revolutionize',
    label: 'revolutionize',
    tier: 'standard',
    re: /\brevolutioni[sz]e\b/i,
  },
  { phrase: 'seamless', label: 'seamless', tier: 'standard', re: /\bseamless(?:ly)?\b/i },
  {
    phrase: 'harness the power',
    label: 'harness the power',
    tier: 'standard',
    re: /\bharness the power\b/i,
  },
  {
    phrase: 'the perfect blend of',
    label: 'the perfect blend of',
    tier: 'standard',
    re: /\bthe perfect blend of\b/i,
  },
  {
    phrase: 'say goodbye to',
    label: 'say goodbye to',
    tier: 'standard',
    re: /\bsay goodbye to\b/i,
  },
  {
    phrase: "it's not just a",
    label: "it's not just a",
    tier: 'standard',
    re: /\bit'?s not just a\b/i,
  },
  {
    phrase: 'a testament to',
    label: 'a testament to',
    tier: 'standard',
    re: /\ba testament to\b/i,
  },
];

/**
 * STANDARD-ONLY, literal entries: every phrase from the original
 * `SLOP_MARKERS` array (caption-directives.ts) not already represented above
 * as its own atomic phrase, kept as an atomic, individually-listable string
 * so the edge prompt clause can still enumerate each one by name. Plus the
 * four `STOCK_VOCABULARY` (this file) entries that appear nowhere else:
 * `paradigm shift`, `ever-evolving`, `tapestry`, `multifaceted`.
 */
const STANDARD_LITERAL_ENTRIES: readonly SlopPhraseEntry[] = [
  literalPhrase('elevate the'),
  literalPhrase('game changer'),
  literalPhrase('deep dive into'),
  literalPhrase('seamlessly'),
  literalPhrase('when it comes to'),
  literalPhrase('nestled in'),
  literalPhrase('paradigm shift'),
  { ...literalPhrase('ever-evolving'), re: /\bever[- ]evolving\b/i },
  literalPhrase('tapestry'),
  literalPhrase('multifaceted'),
];

const ALL_SLOP_PHRASE_ENTRIES: readonly SlopPhraseEntry[] = [
  ...CORE_ENTRIES,
  ...STANDARD_GROUPED_ENTRIES,
  ...STANDARD_LITERAL_ENTRIES,
];

/** Seed of the HARD-FAIL tier. Exactly what `SLOP_VOCABULARY_PATTERN`
 *  (ad-contract.ts) matches today — nothing more. */
export const SLOP_PHRASES_CORE: readonly string[] = CORE_ENTRIES.map((e) => e.phrase);

/** The broader tier for prompt bans and post-hoc warnings. A strict superset
 *  of `SLOP_PHRASES_CORE` — asserted below, not just claimed in a comment. */
export const SLOP_PHRASES_STANDARD: readonly string[] = Array.from(
  new Set(ALL_SLOP_PHRASE_ENTRIES.map((e) => e.phrase)),
);

if (!SLOP_PHRASES_CORE.every((phrase) => SLOP_PHRASES_STANDARD.includes(phrase))) {
  throw new Error(
    'SLOP_PHRASES_CORE must be a strict subset of SLOP_PHRASES_STANDARD — a phrase was added to ' +
      'CORE without also reaching STANDARD.',
  );
}

/**
 * Test `text` against one tier and return every distinct LABEL that matched
 * (first entry to claim a label wins; a later entry sharing that label is
 * skipped, mirroring the dedup-by-label behaviour the original
 * `findSlopMarkers`/`findSceneLanguage` functions already used).
 *
 * `'core'` tests only `SLOP_PHRASES_CORE`'s entries. `'standard'` tests every
 * entry (CORE is a subset of STANDARD, so a CORE phrase still fires under the
 * standard tier — under its own, finer-grained CORE label, not a STANDARD
 * grouped one; see the file comment above for why a caller that wants the
 * grouped labels, such as caption-substance.ts, filters to its own known
 * label set rather than trusting every label this returns).
 */
export function findSlopPhrases(text: string, tier: SlopPhraseTier): SlopPhraseMatch[] {
  const pool = tier === 'core' ? CORE_ENTRIES : ALL_SLOP_PHRASE_ENTRIES;
  const found: SlopPhraseMatch[] = [];
  const seenLabels = new Set<string>();
  for (const entry of pool) {
    if (seenLabels.has(entry.label)) continue;
    if (entry.re.test(text)) {
      seenLabels.add(entry.label);
      found.push({ phrase: entry.phrase, label: entry.label });
    }
  }
  return found;
}

/**
 * Every occurrence of every entry in one tier, with its position. Unlike
 * `findSlopPhrases` nothing is deduplicated: overlapping entries ("deep dive
 * into" and "dive into") both come back, and the caller decides which to keep.
 */
export function findSlopPhraseHits(
  text: string,
  tier: SlopPhraseTier,
): Array<SlopPhraseMatch & { at: number; length: number }> {
  const pool = tier === 'core' ? CORE_ENTRIES : ALL_SLOP_PHRASE_ENTRIES;
  return pool.flatMap((entry) =>
    [...text.matchAll(asGlobal(entry.re))].map((m) => ({
      phrase: entry.phrase,
      label: entry.label,
      at: m.index,
      length: m[0].length,
    })),
  );
}
